'use client'

/**
 * PDP IRL INFLUENCER_CARD PL widget — NON-DS, widget-of-widget child.
 *
 * Renders a single non-DS `InfluencerCard` atom (image + handle + "Buy It Now") from its own proto
 * card data. The list-level concerns — the batched product fetch and the shoppable popup — live on
 * the parent INFLUENCER_SECTION container, which publishes them via `InfluencerCardContext`; this
 * card reads its own product details out of the shared maps and asks the section to open the popup
 * positioned on itself. Rendered through the PL renderer as a child of the section's Swiper.
 */

import { WidgetType } from '@lastbrand/storefront-proto-registry/api/v1/enums'
import { useMemo } from 'react'

import { PLBaseErrorBoundary } from '@src/components/pl-base-error-boundary'
import { PLTypedWidgetInterface, PLTypedWidgetProps } from '@src/components/pl-base-widget'
import InfluencerCard from '@src/quince-components/src/atoms/influencerCard'

import { useInfluencerCardContext } from '../influencer-section/influencer-card-context'
import { adaptInfluencerCard } from '../influencer-section/influencer-section.adapter'

import type { InfluencerCardData } from '@lastbrand/storefront-proto-registry/api/v1/widget'

function InfluencerCardWidgetComponent(props: PLTypedWidgetProps<InfluencerCardData>) {
  const card = props.data.influencerCard
  const ctx = useInfluencerCardContext()

  const influencerDetails = useMemo(() => (card ? adaptInfluencerCard(card) : null), [card])

  if (!influencerDetails) return null

  // Product details (used only for card-impression analytics) come from the section's shared maps.
  const productDetails = ctx
    ? ctx.isB2BSelfService
      ? ctx.contentfulProductIdToProductMap[influencerDetails.contentfulProductId ?? '']
      : influencerDetails.spid != null
        ? ctx.spidToProductMap[influencerDetails.spid]
        : undefined
    : undefined

  return (
    <InfluencerCard
      influencerDetails={influencerDetails}
      productDetails={productDetails}
      productViewTracker={ctx?.productViewTracker}
      onClick={() => ctx?.openPopup()}
    />
  )
}

export const InfluencerCardWidget: PLTypedWidgetInterface<InfluencerCardData, WidgetType.INFLUENCER_CARD> = {
  id: WidgetType.INFLUENCER_CARD,
  errorBoundary: PLBaseErrorBoundary,
  render: InfluencerCardWidgetComponent,
}
